import { useCallback, useEffect, useState } from 'react';
import {
  getDraftCopies,
  getDraftCopyPreviewUrl,
  updateDraftCopyStatus,
} from '../services/taxFilingsService';
import type { TaxFilingDocument } from '../types/taxFilings.types';
import { DRAFT_COPY_STATUS } from '../types/taxFilings.types';
import { useTaxYearStore } from '@/features/tax-year/store/taxYearStore';
import { handleApiError } from '@/lib/utils/errorHandler';
import { toast } from 'sonner';

export function useDraftCopies() {
  const [documents, setDocuments] = useState<TaxFilingDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const effectiveYear = useTaxYearStore((s) => s.getEffectiveYear());
  const configFetched = useTaxYearStore((s) => s.configFetched);

  const fetchDocuments = useCallback(async () => {
    if (effectiveYear == null) return;
    setIsLoading(true);
    setError(null);
    try {
      const list = await getDraftCopies(effectiveYear);
      setDocuments(list);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveYear]);

  useEffect(() => {
    if (!configFetched || effectiveYear == null) return;
    fetchDocuments();
  }, [configFetched, effectiveYear, fetchDocuments]);

  const getPreviewUrl = useCallback(async (documentId: string): Promise<string | null> => {
    try {
      return await getDraftCopyPreviewUrl(documentId);
    } catch (err) {
      handleApiError(err);
      toast.error('Could not open preview');
      return null;
    }
  }, []);

  const approve = useCallback(
    async (documentId: string) => {
      try {
        await updateDraftCopyStatus(documentId, DRAFT_COPY_STATUS.CLIENT_VERIFIED);
        toast.success('Draft copy verified');
        await fetchDocuments();
      } catch (err) {
        handleApiError(err);
        toast.error('Failed to verify draft copy');
        throw err;
      }
    },
    [fetchDocuments]
  );

  const reject = useCallback(
    async (documentId: string, rejectionComment?: string) => {
      try {
        await updateDraftCopyStatus(
          documentId,
          DRAFT_COPY_STATUS.CLIENT_REJECTED,
          rejectionComment
        );
        toast.success('Draft copy rejected');
        await fetchDocuments();
      } catch (err) {
        handleApiError(err);
        toast.error('Failed to reject draft copy');
        throw err;
      }
    },
    [fetchDocuments]
  );

  return {
    documents,
    isLoading: isLoading || !configFetched,
    error,
    refresh: fetchDocuments,
    getPreviewUrl,
    approve,
    reject,
  };
}
